import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/utils/store';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import { Plus, Trash2 } from 'lucide-react';

const ICON_OPTIONS = ['🌐', '📱', '🎨', '🔧', '📦', '🚀', '💻', '🏗️', '📊', '🎯', '📝', '⚡'];
const COLOR_OPTIONS = ['#3b82f6', '#ec4899', '#f59e0b', '#10b981', '#8b5cf6', '#f97316'];
const STATUS_OPTIONS = ['active', 'completed', 'paused'];

export default function AddProjectModal({ open, onClose, project = null, businessId }) {
  const { t } = useTranslation();
  const addProject = useStore((s) => s.addProject);
  const updateProject = useStore((s) => s.updateProject);

  const isEdit = !!project;

  const [name, setName] = useState('');
  const [client, setClient] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('🌐');
  const [color, setColor] = useState('#3b82f6');
  const [status, setStatus] = useState('active');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [budget, setBudget] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && project) {
      setName(project.name || '');
      setClient(project.client || '');
      setDescription(project.description || '');
      setIcon(project.icon || '🌐');
      setColor(project.color || '#3b82f6');
      setStatus(project.status || 'active');
      setStartDate(project.startDate || '');
      setEndDate(project.endDate || '');
      setBudget(project.budget ? String(project.budget) : '');
      setMilestones(project.milestones?.map((m) => ({ ...m })) || []);
      setErrors({});
    } else if (open) {
      setName('');
      setClient('');
      setDescription('');
      setIcon('🌐');
      setColor('#3b82f6');
      setStatus('active');
      setStartDate('');
      setEndDate('');
      setBudget('');
      setMilestones([]);
      setErrors({});
    }
  }, [open, project]);

  const handleAddMilestone = () => {
    setMilestones([...milestones, { id: crypto.randomUUID(), name: '', amount: 0, dueDate: '', paid: false }]);
  };

  const handleUpdateMilestone = (idx, field, value) => {
    setMilestones(milestones.map((m, i) => (i === idx ? { ...m, [field]: value } : m)));
  };

  const handleRemoveMilestone = (idx) => {
    setMilestones(milestones.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    const errs = {};
    if (!name.trim()) errs.name = t('projects.form.nameRequired');
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const data = {
      businessId: project?.businessId || businessId,
      name: name.trim(),
      client: client.trim(),
      description: description.trim(),
      icon,
      color,
      status,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      budget: budget ? Number(budget) : 0,
      milestones: milestones.map((m) => ({
        ...m,
        name: m.name.trim(),
        amount: Number(m.amount) || 0,
      })),
    };

    if (isEdit) {
      updateProject(project.id, data);
    } else {
      addProject(data);
    }
    onClose();
  };

  const statusLabels = {
    active: t('projects.statusActive'),
    completed: t('projects.statusCompleted'),
    paused: t('projects.statusPaused'),
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t('projects.editProject') : t('projects.addNew')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        {/* Name */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('projects.form.name')}
          </label>
          <input
            type="text"
            className={`input ${errors.name ? 'border-danger-500' : ''}`}
            placeholder={t('projects.form.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className="text-xs text-danger-500 mt-1">{errors.name}</p>}
        </div>

        {/* Client */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('projects.form.client')}
          </label>
          <input
            type="text"
            className="input"
            placeholder={t('projects.form.clientPlaceholder')}
            value={client}
            onChange={(e) => setClient(e.target.value)}
          />
        </div>

        {/* Icon */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('projects.form.icon')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {ICON_OPTIONS.map((ic) => (
              <button
                key={ic}
                onClick={() => setIcon(ic)}
                className={`w-10 h-10 rounded-xl text-lg flex items-center justify-center border-2 transition-all ${
                  icon === ic
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 scale-110'
                    : 'border-gray-200 dark:border-gray-700 hover:border-brand-400'
                }`}
              >
                {ic}
              </button>
            ))}
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('projects.form.color')}
          </label>
          <div className="flex gap-2">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-9 h-9 rounded-full transition-all ${
                  color === c ? 'ring-2 ring-offset-2 ring-brand-500 dark:ring-offset-gray-900 scale-110' : ''
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('projects.form.status')}
          </label>
          <div className="flex gap-2">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  status === s
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                }`}
              >
                {statusLabels[s]}
              </button>
            ))}
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('projects.form.startDate')}
            </label>
            <input type="date" className="input" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('projects.form.endDate')}
            </label>
            <input type="date" className="input" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>

        {/* Budget */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('projects.form.budget')}
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-gray-300">$</span>
            <input
              type="number"
              inputMode="decimal"
              placeholder="0"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              className="w-full pl-10 pr-4 py-3 text-lg font-bold input"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('projects.form.description')}
          </label>
          <input
            type="text"
            className="input"
            placeholder={t('projects.form.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Milestones */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('projects.milestones')}
            </label>
            <button
              onClick={handleAddMilestone}
              className="flex items-center gap-1 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 transition-colors"
            >
              <Plus size={14} />
              {t('projects.addMilestone')}
            </button>
          </div>
          {milestones.length === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500">{t('projects.noMilestones')}</p>
          ) : (
            <div className="space-y-3">
              {milestones.map((m, idx) => (
                <div key={m.id || idx} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input flex-1 text-sm"
                      placeholder={t('projects.milestoneName')}
                      value={m.name}
                      onChange={(e) => handleUpdateMilestone(idx, 'name', e.target.value)}
                    />
                    <button
                      onClick={() => handleRemoveMilestone(idx)}
                      className="p-2 rounded-lg text-gray-400 hover:text-danger-500 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] text-gray-400 mb-0.5 block">{t('projects.milestoneAmount')}</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        className="input text-sm"
                        placeholder="0"
                        value={m.amount || ''}
                        onChange={(e) => handleUpdateMilestone(idx, 'amount', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-400 mb-0.5 block">{t('projects.milestoneDueDate')}</label>
                      <input
                        type="date"
                        className="input text-sm"
                        value={m.dueDate || ''}
                        onChange={(e) => handleUpdateMilestone(idx, 'dueDate', e.target.value)}
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={m.paid}
                      onChange={(e) => handleUpdateMilestone(idx, 'paid', e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-600 text-brand-600 focus:ring-brand-500"
                    />
                    {t('projects.milestonePaid')}
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
}
